import { useState, useEffect, useCallback } from "react";
import { authFetch } from "../api";

const RENOVATION_OPTIONS = [
  { value: "", label: "-" },
  { value: "1", label: "1 - Low" },
  { value: "2", label: "2 - Mid-low" },
  { value: "3", label: "3 - Mid" },
  { value: "4", label: "4 - Mid-high" },
  { value: "5", label: "5 - High" },
];

export default function ImageReviewModal({ listing, onClose, onSaved, onNext, onPrev }) {
  const [images, setImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(null);

  // Form state — initialized from listing
  const [dishwasher, setDishwasher] = useState(listing.dishwasher === true || listing.dishwasher === "true");
  const [washerDryer, setWasherDryer] = useState(listing.washer_dryer === "Y" || listing.washer_dryer === true);
  const [outdoorType, setOutdoorType] = useState(listing.outdoor_type || (listing.private_outdoor_space === true || listing.outdoor_space === true ? "unknown" : ""));
  const reno = listing.renovation_level ?? listing.renovation ?? listing.ai_renovation;
  const [renovation, setRenovation] = useState(reno != null ? String(reno) : "");
  const views = listing.premium_views ?? listing.ai_views;
  const [premiumViews, setPremiumViews] = useState(views != null ? String(views) : "");
  const [sqft, setSqft] = useState(listing.sqft || listing.listing_details?.sqft_verified || "");
  const [exteriorSqft, setExteriorSqft] = useState(listing.listing_details?.sqft_exterior || "");

  useEffect(() => {
    loadImages();
  }, [listing.id]);

  const loadImages = async () => {
    setLoading(true);
    try {
      const data = await authFetch(`/listings/${listing.id}/media`);
      // Floor plan first, then photos
      const all = [];
      if (data.floor_plan_url) {
        all.push({ url: data.floor_plan_url, label: "Floor Plan" });
      }
      (data.image_urls || []).forEach((url, i) => {
        all.push({ url, label: `Photo ${i + 1}` });
      });
      setImages(all);
    } catch (err) {
      console.error("Failed to load images:", err);
    }
    setLoading(false);
  };

  const handleKeyDown = useCallback((e) => {
    if (e.key === "ArrowLeft" && currentIndex > 0) setCurrentIndex(currentIndex - 1);
    else if (e.key === "ArrowRight" && currentIndex < images.length - 1) setCurrentIndex(currentIndex + 1);
    else if (e.key === "ArrowUp" && onPrev) { e.preventDefault(); onPrev(); }
    else if (e.key === "ArrowDown" && onNext) { e.preventDefault(); onNext(); }
    else if (e.key === "Escape") onClose();
  }, [currentIndex, images.length, onClose, onNext, onPrev]);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const save = async (field, value) => {
    setSaving(true);
    try {
      const payload = {};
      if (field === "dishwasher") payload.dishwasher = value;
      else if (field === "washer_dryer") payload.washer_dryer = value ? "Y" : "N";
      else if (field === "outdoor_type") payload.outdoor_type = value || "none";
      else if (field === "renovation_level") payload.renovation_level = value || null;
      else if (field === "premium_views") payload.premium_views = value || null;
      else if (field === "sqft" || field === "exterior_sqft") {
        // Save as verified sqft via listing_details
        const intSqft = field === "sqft" ? (value ? parseInt(value) : null) : (sqft ? parseInt(sqft) : null);
        const intExt = field === "exterior_sqft" ? (value ? parseInt(value) : null) : (exteriorSqft ? parseInt(exteriorSqft) : null);
        const details = {
          sqft_verified: intSqft,
          sqft_verified_source: "floor_plan",
        };
        if (intExt) {
          details.sqft_exterior = intExt;
          details.sqft_exterior_source = "floor_plan";
        }
        await authFetch(`/listings/${listing.id}`, {
          method: "PATCH",
          body: JSON.stringify({ listing_details: details }),
        });
        setSavedMsg("Saved");
        setTimeout(() => setSavedMsg(null), 1500);
        if (onSaved) onSaved();
        setSaving(false);
        return;
      }

      await authFetch(`/listings/${listing.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setSavedMsg("Saved");
      setTimeout(() => setSavedMsg(null), 1500);
      if (onSaved) onSaved();
    } catch (err) {
      console.error("Save failed:", err);
      setSavedMsg("Error!");
      setTimeout(() => setSavedMsg(null), 2000);
    }
    setSaving(false);
  };

  const overlayStyle = {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "rgba(0,0,0,0.85)", zIndex: 9999,
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
  };

  const currentImage = images[currentIndex];

  return (
    <div style={overlayStyle}>
      <div
        style={{ maxWidth: 1000, width: "95%", display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginBottom: 12 }}>
          <div style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>
            {onPrev && (
              <button onClick={onPrev} style={{ background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", marginRight: 8 }} title="Previous listing (↑)">&#9650;</button>
            )}
            {onNext && (
              <button onClick={onNext} style={{ background: "none", border: "none", color: "#aaa", fontSize: 18, cursor: "pointer", marginRight: 12 }} title="Next listing (↓)">&#9660;</button>
            )}
            {listing.address || "Listing"} #{listing.unit_number || "?"}
            <span style={{ fontSize: 12, color: "#aaa", marginLeft: 12 }}>ID: {listing.id}</span>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", fontSize: 24, cursor: "pointer" }}>
            &times;
          </button>
        </div>

        {/* Image area */}
        {loading ? (
          <div style={{ color: "#aaa", padding: 40 }}>Loading images...</div>
        ) : images.length === 0 ? (
          <div style={{ color: "#aaa", padding: 40 }}>No images available</div>
        ) : (
          <>
            <div style={{ position: "relative", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {/* Left arrow */}
              {currentIndex > 0 && (
                <button
                  onClick={() => setCurrentIndex(currentIndex - 1)}
                  style={{ position: "absolute", left: 0, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: 32, padding: "20px 12px", cursor: "pointer", borderRadius: 4 }}
                >
                  &#8249;
                </button>
              )}

              <img
                src={currentImage.url}
                alt={currentImage.label}
                style={{ maxHeight: "60vh", maxWidth: "100%", objectFit: "contain", borderRadius: 4 }}
              />

              {/* Right arrow */}
              {currentIndex < images.length - 1 && (
                <button
                  onClick={() => setCurrentIndex(currentIndex + 1)}
                  style={{ position: "absolute", right: 0, background: "rgba(255,255,255,0.2)", border: "none", color: "#fff", fontSize: 32, padding: "20px 12px", cursor: "pointer", borderRadius: 4 }}
                >
                  &#8250;
                </button>
              )}
            </div>

            {/* Image counter + label */}
            <div style={{ color: "#aaa", fontSize: 12, marginTop: 8 }}>
              {currentImage.label} ({currentIndex + 1} / {images.length})
              <span style={{ marginLeft: 12, color: "#666" }}>← → images | ↑ ↓ listings</span>
            </div>
          </>
        )}

        {/* Controls */}
        <div style={{
          display: "flex", gap: 24, marginTop: 16, padding: "16px 24px",
          background: "rgba(255,255,255,0.1)", borderRadius: 8, alignItems: "center",
        }}>
          {/* DW */}
          <label style={{ color: "#fff", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={dishwasher}
              onChange={(e) => {
                setDishwasher(e.target.checked);
                save("dishwasher", e.target.checked);
              }}
              style={{ width: 16, height: 16 }}
            />
            Dishwasher
          </label>

          {/* WD */}
          <label style={{ color: "#fff", fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={washerDryer}
              onChange={(e) => {
                setWasherDryer(e.target.checked);
                save("washer_dryer", e.target.checked);
              }}
              style={{ width: 16, height: 16 }}
            />
            W/D
          </label>

          {/* Outdoor */}
          <label style={{ color: "#fff", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            Outdoor:
            <select
              value={outdoorType}
              onChange={(e) => {
                setOutdoorType(e.target.value);
                save("outdoor_type", e.target.value);
              }}
              style={{ padding: "4px 8px", borderRadius: 4, fontSize: 13 }}
            >
              <option value="">No</option>
              <option value="juliet">Juliet</option>
              <option value="balcony">Balcony</option>
              <option value="terrace">Terrace</option>
              <option value="garden">Garden</option>
              <option value="roof_deck">Roof Deck</option>
              <option value="unknown">Unknown</option>
            </select>
          </label>

          {/* Sqft */}
          <label style={{ color: "#fff", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            Sqft:
            <input
              type="number"
              value={sqft}
              onChange={(e) => setSqft(e.target.value)}
              onBlur={() => save("sqft", sqft)}
              onKeyDown={(e) => { if (e.key === "Enter") save("sqft", sqft); }}
              style={{ width: 70, padding: "4px 8px", borderRadius: 4, fontSize: 13, border: "1px solid #666" }}
            />
          </label>

          {/* Exterior Sqft */}
          <label style={{ color: "#fff", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            Ext:
            <input
              type="number"
              value={exteriorSqft}
              onChange={(e) => setExteriorSqft(e.target.value)}
              onBlur={() => save("exterior_sqft", exteriorSqft)}
              onKeyDown={(e) => { if (e.key === "Enter") save("exterior_sqft", exteriorSqft); }}
              style={{ width: 60, padding: "4px 8px", borderRadius: 4, fontSize: 13, border: "1px solid #666" }}
              placeholder="0"
            />
          </label>

          {/* Reno */}
          <label style={{ color: "#fff", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            Reno:
            <select
              value={renovation}
              onChange={(e) => {
                setRenovation(e.target.value);
                save("renovation_level", e.target.value);
              }}
              style={{ padding: "4px 8px", borderRadius: 4, fontSize: 13 }}
            >
              {RENOVATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>

          {/* Views */}
          <label style={{ color: "#fff", fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
            Views:
            <select
              value={premiumViews}
              onChange={(e) => {
                setPremiumViews(e.target.value);
                save("premium_views", e.target.value);
              }}
              style={{ padding: "4px 8px", borderRadius: 4, fontSize: 13 }}
            >
              <option value="">-</option>
              <option value="0">No</option>
              <option value="1">Yes</option>
            </select>
          </label>

          {/* Save indicator */}
          {saving && <span style={{ color: "#ffd54f", fontSize: 12 }}>Saving...</span>}
          {savedMsg && <span style={{ color: savedMsg === "Saved" ? "#81c784" : "#ef5350", fontSize: 12, fontWeight: 600 }}>{savedMsg}</span>}
        </div>
      </div>
    </div>
  );
}
